// Patch 27 · home search: typing placeholder, "Near me" and a full-screen search with recent searches.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImageUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { entryPath } from "@/lib/paths";
import { entryImage } from "@/lib/placeholders";

const RECENT = "tier_recent_searches";
const CITIES = ["Lisboa", "Porto", "Braga", "Coimbra", "Aveiro", "Faro", "Setúbal", "Leiria"];
const readRecent = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT) || "[]").slice(0, 6);
  } catch {
    return [];
  }
};
const saveRecent = (q) => {
  try {
    const list = [q, ...readRecent().filter((x) => x.toLowerCase() !== q.toLowerCase())].slice(0, 6);
    localStorage.setItem(RECENT, JSON.stringify(list));
  } catch {
    /* storage blocked */
  }
};

/** Placeholder that "types" each phrase, pauses, deletes it and moves to the next. */
function useTyping(phrases) {
  const [txt, setTxt] = useState("");
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setTxt(phrases[0]);
      return;
    }
    let i = 0, n = 0, dir = 1, id;
    const step = () => {
      const p = phrases[i % phrases.length];
      n += dir;
      setTxt(p.slice(0, n));
      let wait = dir > 0 ? 55 : 28;
      if (dir > 0 && n >= p.length) { dir = -1; wait = 1500; }
      else if (dir < 0 && n <= 0) { dir = 1; i += 1; wait = 300; }
      id = setTimeout(step, wait);
    };
    id = setTimeout(step, 500);
    return () => clearTimeout(id);
  }, [phrases]);
  return txt;
}

export function useNearMe() {
  const { t } = useI18n();
  const navigate = useNavigate();
  return () => {
    if (!navigator.geolocation) return navigate("/finder");
    navigator.geolocation.getCurrentPosition(
      (p) => navigate(`/finder?near=${p.coords.latitude.toFixed(3)},${p.coords.longitude.toFixed(3)}`),
      () => toast.error(t.h27.near_denied),
      { timeout: 10000, maximumAge: 600000 }
    );
  };
}

function SearchOverlay({ onClose }) {
  const { t, lang } = useI18n();
  const H = t.h27;
  const navigate = useNavigate();
  const near = useNearMe();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [recent, setRecent] = useState(readRecent);
  const input = useRef(null);
  useEffect(() => {
    input.current?.focus();
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", esc);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) return setRes(null);
    const id = setTimeout(() => api.get("/search", { params: { q: s } }).then(({ data }) => setRes(data.results)).catch(() => setRes([])), 220);
    return () => clearTimeout(id);
  }, [q]);
  const pick = (e) => {
    saveRecent(q.trim() || e.name);
    onClose();
    navigate(entryPath(e));
  };
  const name = (e) => (e.tr?.name?.[lang]) || e.name;

  return createPortal(
    <div className="fixed inset-0 z-[80] bg-[#0A0A0C]/[0.98] tier-pop-soft flex flex-col" role="dialog" aria-modal="true" aria-label={H.search_label} data-testid="search-overlay">
      <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-amber-500/15">
        <Search className="w-5 h-5 text-amber-400 shrink-0" />
        <input
          ref={input}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && res?.[0] && pick(res[0])}
          placeholder={H.search_ph}
          className="flex-1 bg-transparent text-[17px] text-stone-100 placeholder:text-stone-500 outline-none py-2"
          data-testid="search-input"
          enterKeyHint="search"
        />
        <button type="button" onClick={onClose} aria-label={H.close} className="p-2 text-stone-300 hover:text-amber-300" data-testid="search-close">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4">
        {res === null ? (
          <>
            <button type="button" onClick={() => { onClose(); near(); }} className="w-full flex items-center gap-3 border border-amber-400/40 bg-amber-500/[0.07] px-4 py-3 text-left text-amber-100 font-bold" data-testid="search-near">
              <MapPin className="w-5 h-5 text-amber-300" /> {H.near}
            </button>
            {!!recent.length && (
              <div className="mt-6">
                <div className="flex items-center justify-between">
                  <div className="text-[12px] uppercase tracking-[0.18em] font-bold text-amber-400/90">{H.recent}</div>
                  <button type="button" className="text-[13px] text-stone-400 underline" onClick={() => { try { localStorage.removeItem(RECENT); } catch { /* blocked */ } setRecent([]); }}>{H.clear_recent}</button>
                </div>
                <ul className="mt-2">
                  {recent.map((r) => (
                    <li key={r}>
                      <button type="button" onClick={() => setQ(r)} className="w-full flex items-center gap-3 py-2.5 text-left text-[15px] text-stone-200">
                        <Clock className="w-4 h-4 text-stone-500" /> {r}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-6 text-[12px] uppercase tracking-[0.18em] font-bold text-amber-400/90">{H.cities}</div>
            <div className="flex flex-wrap gap-2 mt-3">
              {CITIES.map((c) => (
                <button key={c} type="button" onClick={() => setQ(c)} className="border border-white/15 px-3 py-2 text-[14px] text-stone-200 hover:border-amber-400/60">{c}</button>
              ))}
            </div>
          </>
        ) : res.length === 0 ? (
          <p className="text-[15px] text-stone-400 mt-4">{H.no_results.replace("{q}", q.trim())}</p>
        ) : (
          <ul className="divide-y divide-white/5" data-testid="search-results">
            {res.map((e) => (
              <li key={e.id}>
                <Link to={entryPath(e)} onClick={(ev) => { ev.preventDefault(); pick(e); }} className="flex items-center gap-3 py-3">
                  <img src={resolveImageUrl(entryImage(e), 96)} alt="" className="w-11 h-11 object-cover rounded-sm bg-[#1a1a1f] shrink-0" loading="lazy" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[15px] font-bold text-stone-100 truncate">{name(e)}</span>
                    <span className="block text-[13px] text-stone-400 truncate">{H.cat[e.category] || e.category}{e.city ? ` · ${e.city}` : ""}{e.category === "events" && e.next_date ? ` · ${new Date(e.next_date).toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", { day: "numeric", month: "short" })}` : ""}</span>
                  </span>
                  {e.category !== "events" && e.category !== "creators" && <span className="font-display text-lg text-amber-300 border border-amber-400/50 px-1.5 leading-6">{e.tier}</span>}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>,
    document.body
  );
}

export default function HomeSearch() {
  const { t } = useI18n();
  const H = t.h27;
  const typed = useTyping(H.type);
  const [open, setOpen] = useState(false);
  const near = useNearMe();
  return (
    <>
      <div className="mt-6 flex items-center gap-2 bg-[#141418] border border-amber-500/30 pl-4 pr-2 py-2 max-w-xl" data-testid="home-search">
        <button type="button" onClick={() => setOpen(true)} className="flex-1 min-w-0 flex items-center gap-3 text-left py-1.5" aria-label={H.search_label} data-testid="home-search-open">
          <Search className="w-5 h-5 text-amber-400 shrink-0" />
          <span className="text-[15px] text-stone-400 truncate">{typed}<span className="tier-caret">|</span></span>
        </button>
        <button type="button" onClick={near} className="btn-gold shrink-0 inline-flex items-center gap-1.5 font-bold text-[12px] tracking-[0.06em] uppercase px-3 py-2.5" data-testid="home-near">
          <MapPin className="w-4 h-4" /> {H.near}
        </button>
      </div>
      {open && <SearchOverlay onClose={() => setOpen(false)} />}
    </>
  );
}
